import React, { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useState } from 'react';
import { Box, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { MarketResearchInlineEdit } from '../api/marketResearchEditAwareness';

export type { MarketResearchInlineEdit } from '../api/marketResearchEditAwareness';

interface EditAwarenessContextValue {
  stageCode: number | null;
  changes: MarketResearchInlineEdit[];
  openKey: string | null;
  setOpenKey: (key: string | null) => void;
  mountedSlots: ReadonlySet<string>;
  registerSlot: (key: string) => () => void;
}

const EditAwarenessContext = createContext<EditAwarenessContextValue>({
  stageCode: null, changes: [], openKey: null, setOpenKey: () => undefined,
  mountedSlots: new Set(), registerSlot: () => () => undefined,
});

export const MarketResearchEditAwarenessProvider: React.FC<{
  stageCode: number | null;
  changes: MarketResearchInlineEdit[];
  children: React.ReactNode;
}> = ({ stageCode, changes, children }) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [slotCounts, setSlotCounts] = useState<Record<string, number>>({});
  const registerSlot = useCallback((key: string) => {
    if (key === 'stage') return () => undefined;
    setSlotCounts((counts) => ({ ...counts, [key]: (counts[key] ?? 0) + 1 }));
    return () => setSlotCounts((counts) => {
      const next = { ...counts };
      if ((next[key] ?? 0) <= 1) delete next[key];
      else next[key] -= 1;
      return next;
    });
  }, []);
  const mountedSlots = useMemo(() => new Set(Object.keys(slotCounts)), [slotCounts]);
  const value = useMemo(
    () => ({ stageCode, changes, openKey, setOpenKey, mountedSlots, registerSlot }),
    [stageCode, changes, openKey, mountedSlots, registerSlot],
  );
  return <EditAwarenessContext.Provider value={value}>{children}</EditAwarenessContext.Provider>;
};

const stageSpecificDescription = (stageCode: number | null, action: string): string => {
  const shared: Record<string, string> = {
    stage_updated: 'Stage updated.', stage_generated: 'Stage regenerated.', cleared_for_rework: 'Stage work cleared for rework.',
    questions_regenerated: stageCode === 11 ? 'Synopsis questions regenerated.' : 'Questions regenerated.',
    question_list_edited: 'Question list edited.', answers_changed: stageCode === 11 ? 'Synopsis answers changed.' : 'Answers changed.',
    requirement_document_changed: 'Requirement document added or replaced.',
    requirement_description_changed: 'Requirement description changed.',
    requirement_description_generated: 'Requirement description generated from document.',
    requirement_intake_updated: 'Requirement intake updated.',
    salient_characteristics_regenerated: 'Salient Characteristics regenerated.',
    salient_characteristics_edited: 'Salient Characteristics edited.',
    product_selection_changed: 'Product selection changed.', brand_name_decision_changed: 'Brand-name decision changed.',
    covered_products_changed: 'Covered products changed.', search_parameters_changed: 'Search parameters changed.',
    search_sources_changed: 'Search sources changed.', manual_codes_changed: 'Manual PSC/NAICS inputs changed.',
    product_results_refreshed: 'Product results refreshed.', product_discovery_updated: 'Product Discovery updated.',
    psc_codes_regenerated: 'PSC codes regenerated.', psc_assignments_updated: 'PSC assignments updated.',
    naics_mapping_regenerated: 'NAICS mapping regenerated.', naics_mappings_updated: 'NAICS mappings updated.',
    vendor_search_rerun: 'Vendor search rerun.', vendor_search_criteria_changed: 'Vendor search criteria changed.',
    vendor_results_refreshed: 'Vendor results refreshed.',
    vendor_selections_changed: 'Vendor selections changed.', section_b_regenerated: 'Section B regenerated.',
    section_b_edited: 'Section B edited.', section_c_regenerated: 'Section C regenerated.', section_c_edited: 'Section C edited.',
    section_d_regenerated: 'Section D regenerated.', section_d_edited: 'Section D edited.',
    section_e_regenerated: 'Section E regenerated.', section_e_edited: 'Section E edited.',
    synopsis_regenerated: 'Synopsis regenerated.', synopsis_edited: 'Synopsis edited.',
    line_items_changed: 'Line items changed.', attachments_changed: 'Attachments changed.',
    vendor_quote_files_changed: 'Vendor quote files changed.', bid_information_extracted: 'Bid information extracted.',
    bid_rows_edited: 'Bid rows edited.', vendor_bids_updated: 'Vendor Bids updated.',
    abstract_of_offers_edited: 'Abstract of Offers edited.', dd2579_prefill_refreshed: 'DD/DAF 2579 prefill refreshed.',
    coordination_form_edited: 'Coordination form edited.', coordination_signatures_changed: 'Coordination signatures changed.',
    ja_acquisition_value_changed: 'Acquisition value changed.', ja_certifications_changed: 'Certifications changed.',
    ja_coordination_changed: 'Coordination details changed.', ja_approval_changed: 'Approval changed.',
    ja_narrative_edited: 'J&A narrative edited.', ja_covered_facts_changed: 'Covered products or brand-name facts changed.',
    ja_evidence_changed: 'Supporting evidence changed.', ja_document_regenerated: 'J&A document regenerated.',
    vendor_bid_information_updated: 'Vendor bid information updated.', dd2579_updated: 'DD/DAF 2579 updated.',
    brand_name_ja_updated: 'Brand-name J&A updated.',
  };
  return shared[action] ?? 'Stage updated.';
};

const localizedTimestamp = (value: string): string => new Intl.DateTimeFormat(undefined, {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
}).format(new Date(value));

export interface MarketResearchEditIndicatorSlotProps {
  componentKey: string;
  targetLabel: string;
}

/**
 * A compact, non-destructive marker for an explicitly server-projected MR
 * region. A missing/unknown region is deliberately represented only once at
 * the stage header, never guessed from client payloads or timestamps.
 */
export const MarketResearchEditIndicatorSlot: React.FC<MarketResearchEditIndicatorSlotProps> = ({ componentKey, targetLabel }) => {
  const { stageCode, changes, openKey, setOpenKey, mountedSlots, registerSlot } = useContext(EditAwarenessContext);
  useLayoutEffect(() => registerSlot(componentKey), [componentKey, registerSlot]);
  const selected = changes.filter((change) => (
    change.component_key === componentKey
      || (componentKey === 'stage' && !mountedSlots.has(change.component_key))
  ));
  if (selected.length === 0) return null;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.5, verticalAlign: 'middle' }}>
      {selected.map((change) => (
        <MarketResearchEditIndicator
          change={change}
          description={stageSpecificDescription(stageCode, change.action)}
          key={`${componentKey}-${change.revision}-${change.action}`}
          open={openKey === `${componentKey}-${change.revision}-${change.action}`}
          onOpenChange={(open) => setOpenKey(open ? `${componentKey}-${change.revision}-${change.action}` : null)}
          targetLabel={targetLabel}
          testId={`market-research-edit-indicator-${componentKey}-${change.revision}`}
        />
      ))}
    </Box>
  );
};

const MarketResearchEditIndicator: React.FC<{
  change: MarketResearchInlineEdit;
  description: string;
  targetLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testId: string;
}> = ({ change, description, targetLabel, open, onOpenChange, testId }) => {
  const id = useId();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const buttonLabel = `${targetLabel} updated by another collaborator`;
  const tooltipContent = (
    <Stack spacing={0.25} sx={{ maxWidth: 300 }}>
      <Typography component="span" variant="body2" sx={{ color: 'common.white', fontWeight: 700 }}>{description}</Typography>
      <Typography component="span" variant="caption" sx={{ color: 'common.white' }}>Updated by {change.actor_email} · {localizedTimestamp(change.occurred_at)}</Typography>
    </Stack>
  );
  return (
    <>
      <Tooltip title={tooltipContent} placement="top" disableHoverListener={open}>
        <IconButton
          aria-describedby={open ? id : undefined}
          aria-label={buttonLabel}
          data-edit-awareness-indicator="dot"
          data-edit-awareness-tone="red"
          data-testid={testId}
          onClick={() => onOpenChange(!open)}
          ref={buttonRef}
          size="small"
          sx={{
            borderRadius: '50%', minHeight: 28, minWidth: 28, p: 0.25,
            '&:focus-visible': { outline: '2px solid', outlineColor: 'text.primary', outlineOffset: 2 },
          }}
        >
          <Box
            aria-hidden="true"
            data-testid={`${testId}-dot`}
            sx={{
              backgroundColor: (theme) => theme.palette.marketResearch.editAwareness,
              borderRadius: '50%',
              height: 9,
              width: 9,
            }}
          />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        aria-labelledby={id}
        onClose={() => { onOpenChange(false); buttonRef.current?.focus(); }}
        open={open}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ paper: { sx: { backgroundColor: 'text.primary', color: 'common.white', maxWidth: 'min(360px, calc(100vw - 32px))', p: 1.25 } } }}
      >
        <Box
          id={id}
          role="dialog"
          aria-label={buttonLabel}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onOpenChange(false);
              buttonRef.current?.focus();
            }
          }}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{tooltipContent}</Box>
          <IconButton aria-label="Close update details" onClick={() => { onOpenChange(false); buttonRef.current?.focus(); }} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Popover>
    </>
  );
};
